import { debounce } from 'lodash';
import { Controller } from 'stimulus';
import Swiper, { A11y, Navigation, Pagination } from 'swiper';

Swiper.use([A11y, Navigation, Pagination]);

export default class extends Controller {
  static targets = ['slider', 'pagination', 'next', 'prev'];

  connect() {
    this.initSwiper();

    // Add a debounced resize event listener
    this.debouncedHandleResize = debounce(this.handleResize.bind(this), 100);
    window.addEventListener('resize', this.debouncedHandleResize);
  }

  disconnect() {
    this.destroySwiper();

    // Short-circuit if there is no debounced resize event listener, otherwise remove it
    if (!this.debouncedHandleResize) return;
    window.removeEventListener('resize', this.debouncedHandleResize);
  }

  /**
   * initSwiper
   * - Initialize the Swiper instance
   */
  initSwiper() {
    this.swiper = new Swiper(this.sliderTarget, {
      slidesPerView: 1,
      centeredSlides: true,
      centeredSlidesBounds: true,
      initialSlide: 0,
      spaceBetween: 10,
      autoHeight: true,
      a11y: {
        enabled: true,
      },
      pagination: {
        el: this.paginationTarget,
        clickable: true,
      },
      navigation: {
        nextEl: this.nextTarget,
        prevEl: this.prevTarget,
      },
      speed: 400,
      breakpoints: {
        700: {
          centeredSlides: true,
          slidesPerView: 2,
          initialSlide: 1,
          spaceBetween: 42,
        },
        1200: {
          centeredSlides: false,
          slidesPerView: 3,
          initialSlide: 1,
          spaceBetween: 80,
        },
      },
      on: {
        slideChange: (swiper) => {
          this.updateAriaNotification();
        },
      },
    });

    // Hack-fix for Swiper pagination not updating after a swipe gesture
    this.swiper.on('slideChangeTransitionEnd', () => {
      // Swiper pagination does not hold the correct index when swiping when there are multiple Swiper instances
      // Short-circuit if pagination does not exist
      if (!this.swiper.pagination) return;
      this.swiper.pagination.render();
      this.swiper.pagination.update();
    });

    // Update the aria-notification element with the initial slide details
    this.updateAriaNotification();

    // Update the visibility of the pagination
    this.updateSliderState();
  }

  /**
   * updateAriaNotification
   * - Manually updates the swiper-notification element with slide change details
   */
  updateAriaNotification() {
    // Find the swiper-notification element specific to this Swiper instance
    const notification = this.sliderTarget.querySelector('.swiper-notification');

    // Get the current slide index and total slides
    const currentSlideIndex = this.swiper?.realIndex + 1 ?? -1;
    const totalSlides = this.sliderTarget.dataset?.numSlides ?? this.swiper.slides.length;

    // If it exists and the currentSlideIndex is valid (Swiper is initialized properly)
    if (notification && currentSlideIndex > -1) {
      // Update the innerText of the notification element
      notification.innerText = `Slide ${currentSlideIndex} of ${totalSlides} is active.`;
    }
  }

  /**
   * destroySwiper
   * - Destroy the Swiper instance, if it exists
   */
  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
  }

  /**
   * updateSliderState
   * - Update the visibility of the Swiper pagination based on the number of slides and slidesPerView
   * - Update whether we should add the overriding `not-slider` class if all slides are visible
   */
  updateSliderState() {
    // Short-circuit if the Swiper instance or the pagination target do not exist
    if (!this.swiper || !this.paginationTarget || !this.sliderTarget) return;

    // Get the total number of slides for this Swiper instance
    const totalSlides = this.swiper.slides.length;

    // Determine the current Swiper breakpoint
    const breakpoints = this.swiper.params.breakpoints;
    const currentBreakpoint = this.swiper.currentBreakpoint;

    // Determine the slidesPerView for the current breakpoint,
    //  by searching the breakpoints object with a fallback of the default slidesPerView
    const slidesPerView = breakpoints[currentBreakpoint]?.slidesPerView ?? this.swiper.params.slidesPerView;

    // If all slides are visible
    if (slidesPerView >= totalSlides) {
      // Hide the pagination and apply overriding `not-slider` styles
      this.paginationTarget.classList.add('is-hidden')
      this.sliderTarget.classList.add('not-slider')
    } else {
      // Otherwise, show the pagination and resort the slider styles
      this.paginationTarget.classList.remove('is-hidden')
      this.sliderTarget.classList.remove('not-slider')
    }
  }

  /**
   * handleResize
   * - Handle the window resize event
   */
  handleResize() {
    this.updateSliderState()
  }
}
